import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { IcSearchIcon, IcStreamingIcon, MuteIcon, UnmuteIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IconButton, IconViewSizes } from '@gem/ui-kit';

import { Group } from 'STORE/Groups/Group';
import { Schedule } from 'STORE/Groups/Schedule';
import { Thread } from 'STORE/Groups/Thread';

import ArticleDraftsButton from './ArticleDraftsButton';
import { ChatSettings } from './ChatSettings/ChatSettings';
import { useDefaultHeaderActionsPresenter } from './DefaultHeaderActions.presenter';
import styles from './DefaultHeaderActions.styles.m.css';
import DonateButton from './DonateButton';
import { JoinButton } from './JoinButton/JoinButton';
import { JoinCallButton } from './JoinCallButton/JoinCallButton';
import { StartCallButton } from './StartCallButton/StartCallButton';

interface IDefaultHeaderActionsProps {
  group: Group;
}

export const DefaultHeaderActions: FC<IDefaultHeaderActionsProps> = observer(({ group }) => {
  const presenter = useDefaultHeaderActionsPresenter(group);
  const { t } = useTranslation();

  if (group instanceof Schedule) {
    return null;
  }

  if (group instanceof Thread) {
    return (
      <div className={styles.container}>
        {!presenter.isShowSearch && (
          <IconButton
            className={styles.iconContainer}
            Icon={IcSearchIcon}
            onClick={presenter.showSearch}
            size={IconViewSizes.Big}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {presenter.isGroupJoinButton && (
        <JoinButton
          className={styles.iconContainer}
          groupId={group.id}
        />
      )}

      {presenter.isArticleDrafts && (
        <ArticleDraftsButton
          className={styles.iconContainer}
          groupId={group.id}
        />
      )}

      {presenter.hasDonateButton && <DonateButton className={styles.actionButton} />}

      {presenter.isAvailableStartStream && (
        <IconButton
          className={styles.startStreaming}
          Icon={IcStreamingIcon}
          onClick={presenter.startStream}
          title={t('stream_go_live_button')}
          size={IconViewSizes.Big}
        />
      )}

      {presenter.isAvailableSearch && !presenter.isShowSearch && (
        <IconButton
          className={styles.iconContainer}
          Icon={IcSearchIcon}
          onClick={presenter.showSearch}
          size={IconViewSizes.Big}
        />
      )}

      {!!presenter.backgroundCall && (
        <JoinCallButton
          hasActiveCall={!!presenter.activeCall}
          startTs={Number(presenter.activeCall?.startTs) || 0}
          onClick={presenter.joinToCall}
        />
      )}

      {!presenter.isAvailableJoinCall && presenter.isCreateCallButton && (
        <StartCallButton
          className={styles.iconContainer}
          groupId={group.id}
          startVideoCall={null}
          startAudioCall={presenter.audioCall}
          scheduleCallEvent={presenter.scheduleCallEvent}
          startConferenceCall={presenter.isAvailableConference ? presenter.conferenceCall : null}
        />
      )}

      {group.notificationsAvailable && !group.isServiceMessages && (
        <IconButton
          className={styles.iconContainer}
          Icon={group.muted ? UnmuteIcon : MuteIcon}
          onClick={presenter.onToggleMute}
          size={IconViewSizes.Big}
        />
      )}
      <ChatSettings />
    </div>
  );
});
